import React, { useEffect, useState } from "react";
import { ColorRectangle } from "../components/ColorRectangle";
import {
  useAppDispatch,
  mixActions,
  mixColors,
  AppDispatch,
} from "../../../store";
import { MixState } from "../store/MixReducer";
import { StyleSheet, View } from "react-native";
import { Checklist, Body, Text } from "../../../components";
import { Icons, Sizing, useTheme } from "../../../theme";
import ColorPicker, { Panel4 } from "reanimated-color-picker";
import { colorPickerStyle } from "../components/colorPickerStyle";
import { LinearGradient } from "expo-linear-gradient";
import { colorPercentDiff } from "./messageUtils";

export const getHelpPopup = {
  timerDuration: 60 * 999,
  title: "Oh du scheinst bisschens lost!",
  icon: <Icons.HeadQuestion size="huge" />,
  activateConfetti: false,
  children: (
    <Body
      text="Aber das ist gar kein Problem, einer der Studis hilft dir gerne :) Melde dich fix und wir helfen dir sofort."
      style={{ width: "80%", textAlign: "center" }}
    />
  ),
};

export const SelectEveKnowledge = ({
  mix,
  dispatch,
}: {
  mix: MixState;
  dispatch: AppDispatch;
}) => {
  const [countTries, setCountTries] = useState<number>(0);

  // Help Popup nach 10 Eingaben
  useEffect(() => {
    if (countTries >= 10) {
      dispatch(mixActions.showPopup(getHelpPopup));
      setCountTries(0);
    }
  }, [countTries]);

  return (
    <>
      <Checklist
        tries={countTries}
        setTries={setCountTries}
        onValidationChange={(isCorrect) => {
          dispatch(
            mixActions.updateProgress({ eveKnowledgeGuessedRight: isCorrect })
          );
        }}
        options={[
          {
            id: "gemeinsame",
            component: (
              <ColorRectangle color={mix.generator}>
                Gemeinsame Farbe
              </ColorRectangle>
            ),
            shouldBeMarked: true,
          },
          {
            id: "secrets",
            component: (
              <>
                <ColorRectangle color={mix.alice.secret}>
                  Alice Geheim
                </ColorRectangle>
                <ColorRectangle color={mix.bob.secret}>
                  Bob Geheim
                </ColorRectangle>
              </>
            ),
            shouldBeMarked: false,
          },
          {
            id: "mixes",
            component: (
              <>
                <ColorRectangle color={mix.alice.mixed}>
                  Mix Alice
                </ColorRectangle>
                <ColorRectangle color={mix.bob.mixed}>Mix Bob</ColorRectangle>
              </>
            ),
            shouldBeMarked: true,
          },
          {
            id: "result",
            component: (
              <ColorRectangle color={mix.alice.result}>
                Geheime Farbe
              </ColorRectangle>
            ),
            shouldBeMarked: false,
          },
        ]}
      />
      <Text>
        <Icons.Information style={{ marginRight: Sizing.cs(10) }} />
        Eve kennt nur die Farben, die sich Alice & Bob geschickt haben.
      </Text>
    </>
  );
};

export const EveColorGuesser = ({ mix }: { mix: MixState }) => {
  const [selectedColor, setSelectedColor] = useState("#03FDE0");
  const [countSelected, setCountSelected] = useState(0);
  const [mixedColor, setMixedColor] = useState(
    mixColors([selectedColor, mix.bob.mixed])
  );

  const { baseOnContrast } = useTheme();
  const dispatch = useAppDispatch();

  const onSelectColor = ({ hex }: { hex: string }) => {
    setSelectedColor(hex);
    setMixedColor(mixColors([hex, mix.bob.mixed]));

    setCountSelected((oldCount) => oldCount + 1);
    if (countSelected + 1 >= 5) {
      dispatch(mixActions.updateProgress({ eveFailedBruteforce: true }));
    }
  };

  const styles = StyleSheet.create({
    colors: {
      width: Sizing.cs(250),
      height: Sizing.cs(50),
      borderRadius: 10,
    },
  });

  return (
    <View
      style={{
        flexDirection: "column",
        alignItems: "center",
        opacity: mix.msgProgress.eveFailedBruteforce ? 0.3 : 1,
        pointerEvents: mix.msgProgress.eveFailedBruteforce ? "none" : "auto",
      }}
    >
      <ColorPicker
        style={{ width: Sizing.cs(250), height: Sizing.cs(200) }}
        value={selectedColor}
        onComplete={onSelectColor}
      >
        <Panel4 style={colorPickerStyle.panelStyle} thumbShape="ring" />
      </ColorPicker>

      <Text>+</Text>

      <ColorRectangle color={mix.bob.mixed} style={styles.colors}>
        <Body color={baseOnContrast(mix.bob.mixed)}>Bob Mix</Body>
      </ColorRectangle>

      <Text>=</Text>

      <LinearGradient
        style={[
          {
            borderRadius: Sizing.cs(50),
            justifyContent: "center",
            alignItems: "center",
          },
          styles.colors,
        ]}
        locations={[0.1, 0.2, 0.8, 0.9]}
        start={[0, 0]}
        end={[1, 0]}
        colors={[selectedColor, mixedColor, mixedColor, mix.bob.mixed]}
      >
        <Body color={baseOnContrast(mixedColor)}>...soll Geteilt Geheim</Body>
      </LinearGradient>

      <Body style={{ marginTop: Sizing.cs(10) }}>
        {(100.0 - colorPercentDiff(selectedColor, mix.alice.secret)).toFixed(2)}
        % nah dran
      </Body>
    </View>
  );
};
